import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { IconButton, Row } from "@narmi/design_system";
import { RefObject } from "react";
import { PanelBoundary } from "../components/PanelBoundary";
import { PanelType, Thread } from "../types";
import { AccountsPanel } from "./AccountsPanel";
import { RecentMessages } from "./RecentMessages";
import styles from "./style.module.css";
import { ThreadMetadata } from "./ThreadMetadata";
import { ThreadUserDetails } from "./ThreadUserDetails";
import { UserNotes } from "./UserNotes";

export type PanelRefs = Record<PanelType, RefObject<HTMLDivElement | null>>;

export function ThreadDetailsPanel({
  thread,
  panelRefs,
}: {
  thread: Thread;
  panelRefs: PanelRefs;
}) {
  const { permissions, features } = useUserConfiguration();

  // Each section fetches independently, so each gets its own boundary — a
  // failure in one panel must not take down the rest of the page.
  return (
    <div className={styles.detailsPanel}>
      <div className="fontWeight--bold">Details</div>
      <div className={styles.detailsPanelDetailsContainer}>
        <div ref={panelRefs.data} className={styles.detailsPanelSection}>
          <PanelRow iconName="list" label="Data" />
          <PanelBoundary label="message details">
            <ThreadMetadata thread={thread} />
          </PanelBoundary>
        </div>
        <div ref={panelRefs.user}>
          <PanelBoundary label="user information">
            <ThreadUserDetails thread={thread} />
          </PanelBoundary>
        </div>
        {permissions.includes("indigo.view_account") && (
          <div ref={panelRefs.accounts} className={styles.detailsPanelSection}>
            <PanelRow
              iconName="bank"
              label="Accounts"
              href={
                features.includes("customer_profile")
                  ? `/users/${thread.user.id}`
                  : `/users/${thread.user.id}/old/`
              }
            />
            <PanelBoundary label="accounts">
              <AccountsPanel userId={thread.user.id} />
            </PanelBoundary>
          </div>
        )}
        <div ref={panelRefs.messages} className={styles.detailsPanelSection}>
          <PanelRow iconName="message-square" label="Recent messages" />
          <PanelBoundary label="recent messages">
            <RecentMessages thread={thread} />
          </PanelBoundary>
        </div>
        <div ref={panelRefs.notes} className={styles.detailsPanelSection}>
          <PanelRow iconName="edit-3" label="Notes" href={`/users/${thread.user.id}/notes`} />
          <PanelBoundary label="notes">
            <UserNotes thread={thread} />
          </PanelBoundary>
        </div>
      </div>
    </div>
  );
}

export const PanelRow = ({
  iconName,
  label,
  href,
}: {
  iconName: string;
  label: string;
  href?: string;
}) => {
  return (
    <Row alignItems="center" justifyContent="space-between" className="margin--bottom--m">
      <Row.Item>
        <Row alignItems="center" gapSize="xs">
          <Row.Item shrink>
            <span className={`narmi-icon-${iconName} fontColor--secondary`} />
          </Row.Item>
          <Row.Item shrink>
            <div className="fontColor--primary fontSize--xs">{label.toUpperCase()}</div>
          </Row.Item>
        </Row>
      </Row.Item>
      {href && (
        <Row.Item shrink>
          <IconButton
            className=""
            name="external-link"
            kind="action"
            textSize="m"
            as="a"
            target="_blank"
            rel="noopener noreferrer"
            href={href}
          />
        </Row.Item>
      )}
    </Row>
  );
};
